import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { IChangeAwareComponent, EditingComponent, FormService } from '@wephone-utils';
import { UserEntity } from '@wephone-core/model/entity/user';
import { FormBuilder, FormControl } from '@angular/forms';
import { AccessRight, FeatureName } from '@wephone-core/system';
import { ConfigManager } from '@wephone-core/wephone-core.module';
import * as _ from 'lodash';
import { UserRepository } from '@wephone-core/model/repository/user';
import { IUserEntity } from '@wephone-core/model/entity/user.i';

@Component({
  selector: 'app-user-access-right',
  templateUrl: './user-access-right.component.html',
  styleUrls: ['./user-access-right.component.scss']
})
export class UserAccessRightComponent extends EditingComponent implements OnInit, OnChanges, IChangeAwareComponent {
  @Input() userData: IUserEntity;
  @Output() readonly formValueChanges: EventEmitter<boolean>;

  user: UserEntity;
  allOptionChecked = false;
  private userAccessRights: Record<string, boolean> = {};

  featureLabels = {
    LIVEPAGE: 'acl.feature_name.page_live',
    CALLHISTORY: 'acl.feature_name.call_history',
    EAVESDROP: 'acl.feature_name.eavesdrop',
    RECORDING: 'acl.feature_name.recording',
    VOICEMAIL: 'acl.feature_name.voicemail',
    STATS: 'acl.feature_name.stats',
  };

  constructor(
    public formService: FormService,
    protected fb: FormBuilder,
  ) {
    super();
    this.formValueChanges = new EventEmitter<boolean>();
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userData && changes.userData.currentValue) {
      this.initialize();
    }
  }

  initialize(): void {
    if (!this.userData) {
      return;
    }

    this.user = UserRepository.getInstance<UserRepository>().create(this.userData) as UserEntity;

    this.userAccessRights = this.getUserAccessRights();
    this.createForm();
  }

  private getUserAccessRights(): Record<string, boolean> {
    const isGrantedAccess = this.user.isGrantedAccess.bind(this.user);
    return {
      LIVEPAGE: this.hasLivePage ? isGrantedAccess(AccessRight.LivePage) : false,
      CALLHISTORY: isGrantedAccess(AccessRight.CallHistory),
      EAVESDROP: isGrantedAccess(AccessRight.Eavesdrop),
      RECORDING: isGrantedAccess(AccessRight.Recording),
      VOICEMAIL: this.hasVoicemail ? isGrantedAccess(AccessRight.VoiceMail) : false,
      STATS: isGrantedAccess(AccessRight.Stats),
    };
  }

  private createForm(): void {
    this.form = this.fb.group(
      Object.keys(this.userAccessRights)
        .filter(feature => this.userAccessRights[feature] !== undefined)
        .reduce((objControls, feature) => {
          if (feature === 'LIVEPAGE' && !this.hasLivePage || feature === 'VOICEMAIL' && !this.hasVoicemail) {
            return objControls;
          }

          objControls[feature] = new FormControl(this.userAccessRights[feature]);
          return objControls;
        }, {})
    );

    this.addSubscription(
      this.form.valueChanges.subscribe(() => {
        this.updateAllOptionChecked();
        this.formValueChanges.emit(this.form.dirty);
      })
    );

    this.updateAllOptionChecked();
  }

  get hasLivePage(): boolean {
    return this.user && !this.user.isAgent() && !this.user.isUser();
  }

  get hasVoicemail(): boolean {
    return this.user && !this.user.isWatcher();
  }

  resetForm(): void {
    this.initialize();
    // this.createForm();
    // this.form.reset(this.userAccessRights);
    // this.form.markAsPristine();
    this.onFormValueChange();
  }

  onFormValueChange(): void {
    this.formValueChanges.next(this.formHasChanged());
  }

  getChangeSet(): any {
    return this.formService.getChangeSet(this.form);
  }

  updateAllOptionChecked(): void {
    this.allOptionChecked = Object.keys(this.form.controls).every(key => this.form.get(key).value);
  }

  isValid(): boolean {
    return true;
  }

  get anyOptionChecked(): boolean {
    return Object.keys(this.form.controls).some(key => this.form.get(key).value) && !this.allOptionChecked;
  }

  setAll(access: boolean): void {
    this.allOptionChecked = access;
    const result: any = {
      LIVEPAGE: this.hasLivePage ? access : false,
      CALLHISTORY: access,
      EAVESDROP: access,
      RECORDING: access,
      VOICEMAIL: this.hasVoicemail ? access : false,
      STATS: access,
    };

    Object.keys(this.form.controls).forEach(key => this.form.get(key).markAsDirty());
    this.form.patchValue(result, { emitEvent: true });
  }
}
